<template xmlns:v-tooltip="http://www.w3.org/1999/xhtml">
  <div>
    <slot v-if="viewingAsCompany && editing" name="update-contract-notices"></slot>

    <BaseHtmlForm
      id="contract-form"
      ref="contractForm"
      :action="formAction"
      :method="formMethod"
      append-user-and-account
      @form-submitted="submitting = true"
    >
      <input :value="offerId" type="hidden" name="offer_id" />

      <WdsAlert v-if="canceledJob" warning icon class="m-b-20">
        {{
          $t('This contract cannot be accepted, because :clientname has ended this job.', {
            clientname: company?.company ?? $t('deleted company'),
          })
        }}
      </WdsAlert>
      <WdsAlert v-else-if="formHeaderText && !acceptDisabled" info icon class="m-b-20">
        {{ $t(formHeaderText) }}
      </WdsAlert>

      <WdsAlert v-if="hireApprovalStateIsPending && viewingAsCompany" warning class="m-b-20">
        <div v-if="approvalTriggers.length > 0" class="m-b-4">
          {{ $t('Approval triggered due to:') }}
        </div>
        <div v-else class="m-b-4">{{ $t('Approval triggered') }}</div>
        <div v-for="(approvalTrigger, key) in approvalTriggers" :key="key">
          <div class="tw-text-black">
            {{ approvalTrigger.fieldTitle }} {{ approvalTrigger.ruleOperator }}
            {{ approvalTrigger.ruleValue }}
            <br />
          </div>
        </div>
      </WdsAlert>

      <div
        v-if="viewingAsCompany && editing"
        id="advancedFieldsWrapper"
        class="text-right hidden-xs"
      >
        <ButtonDropdown v-cloak class="m-b-10" :size="'xs'" type="button" color-scheme="default">
          <a type="button">
            {{ $t('Add advanced field') }}
          </a>
          <template #dropdown>
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <button-split-list>
              <li>
                <a href="#" @click="addAdvanceField('conditions')">
                  {{ $t('Add additional terms') }}
                </a>
              </li>
              <li>
                <a href="#" @click="addAdvanceField('attachments')">
                  {{ $t('Add files') }}
                </a>
              </li>
              <li>
                <a href="#" @click="addAdvanceField('custom_invoice_text')">
                  {{ $t('Add custom invoice text') }}
                </a>
              </li>
              <li v-if="companyContactsCount > 1">
                <a href="#" @click="addAdvanceField('bill_contact_user_id')">
                  {{ $t('Add billing contact person') }}
                </a>
              </li>
            </button-split-list>
          </template>
        </ButtonDropdown>
      </div>

      <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
      <contract-form-fields
        ref="contractFormFields"
        :purpose="purpose"
        :contract-built-from-other-models="contractBuiltFromOtherModels || {}"
        :active-contract="activeContract || {}"
        :draft-contract="draftContract || {}"
        :is-accepted="isAccepted"
        :perspective="perspective"
        :company="company"
        :company-currency-config="companyCurrencyConfig"
        :freelancer-currency-config="freelancerCurrencyConfig"
        :editable="editing"
        :company-contacts="companyContacts"
        :immediately-savable-fields="immediatelySavableFields"
        :minimum-hourly-rate-company="1"
        :minimum-fixed-rate-company="1"
        :minimum-hourly-rate-freelancer="1"
        :minimum-fixed-rate-freelancer="1"
        :can-edit-rate="canEditRate"
        :advanced-fields="advancedFields"
        :job="job"
        :hire-id="hireId"
        :has-bills="hasBills"
        :require-date-start="requireDateStart"
        :require-date-end="requireDateEnd"
        :require-po-number="requirePoNumber"
        :can-view-contract="canViewContract"
        :terms-accepted="termsAccepted"
        :has-multiple-business-entities="hasMultipleBusinessEntities"
        :has-milestones="hasMilestones"
        @currency-change="currencyChanged"
        @changes-added="changesAdded"
        @changes-cleared="changesCleared"
        @changes-counted="changesCounted"
        @changes-needing-approval-added="changesNeedingApprovalAdded"
        @changes-needing-approval-cleared="changesNeedingApprovalCleared"
        @toggled-warnings="toggleWarnings"
      />

      <div class="row">
        <div v-if="showRecruiterAttributionSection" class="col-sm-12 m-b-15">
          <strong>
            {{ $t('Has this freelancer been placed on this job by a recruiter?') }}
          </strong>

          <div v-if="submittingSupplier === null">
            <div class="radio">
              <label>
                <input
                  v-model="recruiterAttribution"
                  type="radio"
                  name="recruiter_attribution"
                  :value="false"
                />
                {{ $t('No, there was no recruiter involved in this case') }}
                <i class="input-helper"></i>
              </label>
            </div>

            <div class="radio">
              <label>
                <input
                  v-model="recruiterAttribution"
                  type="radio"
                  name="recruiter_attribution"
                  :value="true"
                />
                {{ $t('Yes, I need to attribute a recruiter to this freelancer contract') }}
                <i class="input-helper"></i>
              </label>
            </div>
          </div>

          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <select-attribute-recruiter
            v-if="recruiterAttribution"
            :active-recruiter="submittingSupplier"
            :company-recruiters="companyRecruiters"
            :change-fee="true"
            :change-ownership="true"
            :decimal-point="decimalPoint"
            @update:recruiter-id="recruiterId = $event"
            @update:fee-in-percent="feeInPercent = $event"
            @update:ownership-days="ownershipDays = $event"
            @update:ownership-start-date="ownershipStartDate = $event"
          />
        </div>

        <div class="col-sm-12 text-right">
          <a
            v-if="
              (viewingAsUser || viewingAsStaffingAgency) && purpose === 'approve' && !canceledJob
            "
            class="c-black f-14"
          >
            {{ $t('Need other changes?') }}
            &nbsp;
            <WdsTooltip
              :top="true"
              :text="
                $t(`If you need other changes made before accepting,
              please request them from the company in your conversation`)
              "
            >
              <i class="zmdi zmdi-help" />
            </WdsTooltip>
          </a>
          <a
            v-else-if="
              (viewingAsUser || viewingAsStaffingAgency) && purpose === 'show' && !canceledJob
            "
            class="c-black f-14"
          >
            {{ $t('Need other changes?') }}
            &nbsp;
            <WdsTooltip
              :top="true"
              :text="
                $t(
                  `If you need to change any contract details, please request them from the company in your conversation.`,
                )
              "
            >
              <i class="zmdi zmdi-help" />
            </WdsTooltip>
          </a>
        </div>

        <div v-if="shouldShowSignature" class="col-sm-12">
          <ContractFormSignature
            :disabled="acceptDisabled || (isAccepted && !isApproving)"
            :initial-signature="activeContract.freelancer_signature || freelancerDefaultSignature"
            :is-signed="!!activeContract.freelancer_signature && isAccepted && !isApproving"
            :signed-at="acceptedAt"
          />
        </div>

        <div v-if="isApproving && !canceledJob" class="col-sm-12">
          <ContractFormAcceptTermInput
            :disabled="acceptDisabled"
            :accepting-new-contract="isAccepted"
            @accepted="handleTermsAccepted"
          />
        </div>
      </div>

      <div
        v-if="
          purpose === 'create' &&
          viewingAsCompany &&
          (company.is_trusted || company.plan === 'enterprise')
        "
        class="pull-left"
      >
        <div class="checkbox">
          <div class="checkbox m-t-0 m-b-5">
            <label>
              <input v-model="standardContract" name="use_contract" type="checkbox" />
              <i class="input-helper" />
              <strong>{{ $t('Include Worksome standard contract') }}</strong>
            </label>
          </div>
        </div>
      </div>
      <input v-else v-model="standardContract" type="hidden" name="use_contract" />

      <slot name="before-action-buttons" :editing="editing" :action="action"></slot>

      <ContractFormActionButtons
        :close-button-text="purpose === 'edit' ? 'Cancel' : 'Close'"
        :show-request-button="showRequestButton"
        :request-button-text="changeRequestDisplayText"
        :show-cancel-request-button="canCancelPendingRequest"
        :show-undo-button="showUndoAllButton && changesCount"
        :undo-button-text="$t(`Undo all (:count) changes`, { count: changesCount })"
        :show-submit-button="showSubmitButton"
        :submit-button-disabled="actionButtonDisabled"
        :submit-button-text="formButtonText"
        :submit-button-tooltip="formButtonTooltip"
        :is-submitting="submitting"
        :contract-number="hireId"
        :uses-fl-currency="showCurrencyAlert"
        :validation-errors="backEndErrors"
        @clear-errors="clearErrors"
        @close="$emit('close')"
        @edit="edit"
        @undo="$refs.contractFormFields.resetToInitialFull()"
        @cancel-request="cancelChangeRequest"
        @submit-form="onSubmitForm"
      />
    </BaseHtmlForm>
  </div>
</template>

<script>
import { nextTick } from 'vue'
/**
 * Component used for 4 scenarios which allow different actions from company / freelancer:
 * Hire has no contract (i.e. purpose === 'create'):
 *   company: show data built from other models, allow changes, submit form to create draft contract
 *   freelancer: n/a
 * Hire has only draft contract:
 *   company: allow changes/updates to draft
 *   freelancer: show draft and allow accepting draft to create active contract
 * Hire has only active contract:
 *   company: allow creation of change requests, submit form to create a draft
 *   freelancer: show active contract details
 * Hire has an active contract + draft contract:
 *   company: allow updating/cancelling the draft
 *   freelancer: show draft and allow accepting draft to create new active contract
 *
 * note: there are some fields which do not require approval from freelancers when updating so no draft is created
 */

import { WdsAlert, WdsTooltip } from '@worksome/wds-components'
import ContractFormSignature from '@/components/ContractFormSignature.vue'
import ContractFormAcceptTermInput from '@/components/ContractFormAcceptTermInput.vue'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import ContractFormActionButtons from '@/components/ContractFormActionButtons.vue'

export default {
  components: {
    WdsAlert,
    WdsTooltip,
    ContractFormSignature,
    ContractFormAcceptTermInput,
    BaseHtmlForm,
    ButtonDropdown,
    ContractFormActionButtons,
  },

  apollo: {
    $client: 'publicApiCustomFields',
  },

  inject: ['approvalApprovableId', 'reloadApprovalApprovables'],

  props: {
    action: { type: String, default: '' },
    contractBuiltFromOtherModels: { type: Object, default: () => {} },
    activeContract: { type: Object, default: () => {} },
    draftContract: { type: Object, default: () => {} },
    immediatelySavableFields: { type: Array, default: () => [] },
    perspective: { type: String, default: 'company' },
    company: { type: Object, default: () => {} },
    companyCurrencyConfig: { type: Object, default: () => {} },
    freelancerCurrencyConfig: { type: Object, default: () => {} },
    companyRole: { type: String, default: '' },
    isAccepted: { type: Boolean, default: false },
    isDirectHire: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    editable: { type: Boolean, default: true },
    acceptDisabled: { type: Boolean, default: false },
    hireId: { type: Number, default: 0 },
    yjobId: { type: Number, default: 0 },
    offerId: { type: Number, default: 0 },
    submittingSupplier: { type: Object, default: null },
    rateType: { type: String, default: '' },
    companyContacts: { type: Object, required: true },
    companyRecruiters: { type: Array, required: true },
    minimumHourlyRateCompany: { type: Number, required: true },
    minimumFixedRateCompany: { type: Number, required: true },
    minimumHourlyRateFreelancer: { type: Number, required: true },
    minimumFixedRateFreelancer: { type: Number, required: true },
    job: { type: Object, default: () => {}, required: false },
    dialogIsClosed: { type: Boolean, default: false },
    hasBills: { type: Boolean, default: false },
    requireDateStart: { type: Boolean, default: false },
    requireDateEnd: { type: Boolean, default: false },
    requirePoNumber: { type: Boolean, default: false },
    hasHiresWithCompanyRecruiter: { type: Boolean, default: false },
    hasExpiredHires: { type: Boolean, default: false },
    decimalPoint: { type: String, default: '' },
    freelancerDefaultSignature: { type: String, default: '' },
    canViewContract: { type: Boolean, default: false },
    acceptedAt: { type: String, default: '' },
    hireApprovalState: { type: String, default: 'unknown' }, // Enum from ApprovalApprovableState.php
    approvalTriggers: { type: Array, default: () => [] },
    hasMultipleBusinessEntities: { type: Boolean, default: false },
    staffingAgencyManagesWorker: { type: Boolean, default: false },
    hasMilestones: { type: Boolean, default: false },
  },

  emits: ['close'],

  data() {
    return {
      internalAction: this.action,
      editing: false,
      hasChanges: false,
      submitting: false,
      hasValidationErrors: false,
      cancellingChangeRequest: false,
      hasChangesNeedingApproval: false,
      standardContract: true,
      advancedFields: [],
      recruiterAttribution: this.submittingSupplier === null ? null : true,
      recruiterId: null,
      feeInPercent: 0,
      ownershipDays: 0,
      ownershipStartDate: null,
      changesCount: 0,
      termsAccepted: false,
      currencyConfig: null,
      hasApprovalChanges: false,
      backEndErrors: {},
    }
  },
  computed: {
    purpose: {
      get() {
        return this.internalAction ?? this.action
      },
      set(value) {
        this.internalAction = value
      },
    },
    viewingAsCompany() {
      return this.perspective === 'company'
    },

    viewingAsStaffingAgency() {
      return this.perspective === 'recruiter'
    },
    canceledJob() {
      return this.dialogIsClosed && !this.isAccepted
    },
    formButtonText() {
      if (this.submitting) {
        return 'Processing'
      }
      if (this.purpose === 'create') {
        return 'Accept the bid'
      }
      if (this.isApproving) {
        return this.isAccepted ? 'Accept new contract' : 'Accept job'
      }
      // Hire isnt reviewed & accepted yet, so submitting form will only update the request
      // or there are changes only to fields that require freelancer approval
      if (
        !this.isAccepted ||
        (this.hasChanges && !this.hasChangesNeedingApproval) ||
        !this.hasChanges
      ) {
        return 'Save changes'
      }
      return this.hasDraftContract ? 'Update request' : 'Send request'
    },
    viewingAsUser() {
      return this.perspective === 'user'
    },
    hasActiveContract() {
      return this.activeContract && Object.keys(this.activeContract).length > 0
    },
    hasDraftContract() {
      return this.draftContract && Object.keys(this.draftContract).length > 0
    },
    isRequestingContractChanges() {
      return (
        (this.purpose === 'show' && this.editing) ||
        (this.purpose !== 'create' && this.purpose !== 'show' && this.purpose !== 'approve')
      )
    },
    formHeaderText() {
      if (this.hireApprovalStateIsPending) {
        return ''
      }

      if (this.hasActiveContract && this.hasDraftContract && this.viewingAsCompany) {
        return 'Awaiting freelancer acceptance of contract changes'
      }

      if (this.isRequestingContractChanges) {
        const text = 'Request changes to the contract'
        if (this.requirePoNumber) {
          return `${text}. Please ensure that any changes in the cost falls within the allocated cost for the PO`
        }
        return text
      }

      if (this.purpose === 'create') {
        return 'Review the contract details'
      }

      if (this.purpose === 'approve') {
        return this.isAccepted
          ? 'Accept the updated contract details'
          : 'Accept the contract details'
      }

      return this.isAccepted ? '' : 'Awaiting final acceptance by the freelancer'
    },
    formButtonTooltip() {
      if (this.canceledJob) {
        return this.$t(
          'This contract cannot be accepted, because :clientname has ended this job.',
          {
            clientname: this.company?.company ?? this.$t('deleted company'),
          },
        )
      }
      if (this.purpose !== 'create' && !this.isApproving) {
        return this.hasChangesNeedingApproval
          ? 'A change request will be sent to the freelancer for approval'
          : 'The changes will be saved and applied to all invoices on this job'
      }
      return ''
    },
    changeRequestDisplayText() {
      return this.isAccepted ? 'Request changes' : 'Make changes'
    },
    formAction() {
      if (this.cancellingChangeRequest) {
        return `/hires/${this.hireId}/cancel-changes`
      }
      if (this.isApproving) {
        return `/hires/${this.hireId}/accept`
      }
      if (this.purpose === 'create' && this.offerId) {
        return this.submittingSupplier
          ? `/offer/${this.offerId}/hires-from-supplier`
          : `/offer/${this.offerId}/hires`
      }
      return `/hires/${this.hireId}`
    },
    formMethod() {
      if (this.isApproving || this.purpose === 'create') {
        return 'POST'
      }
      return 'PATCH'
    },
    isApproving() {
      return this.purpose === 'approve'
    },
    actionButtonDisabled() {
      if (this.canceledJob) {
        return true
      }

      if (this.acceptDisabled) {
        return true
      }

      if (this.submitting || this.hasValidationErrors) {
        return true
      }

      if (this.hasDraftContract && this.viewingAsCompany) {
        // there are changes to approval
        if (this.hasApprovalChanges) {
          return false
        }

        return !this.hasChanges
      }

      if (this.showRecruiterAttributionSection) {
        if (
          (this.recruiterAttribution === null || this.recruiterAttribution) &&
          (!this.recruiterId ||
            this.feeInPercent < 0 ||
            this.ownershipDays < 0 ||
            !this.ownershipStartDate)
        ) {
          return true
        }
      }

      if (!this.viewingAsCompany && !this.termsAccepted) {
        return true
      }
      return this.purpose !== 'create' && !this.hasChanges && this.purpose !== 'approve'
    },
    showSubmitButton() {
      return (
        (this.editing ||
          (this.viewingAsCompany && this.hasDraftContract) ||
          (this.viewingAsStaffingAgency &&
            this.staffingAgencyManagesWorker &&
            this.purpose === 'approve') ||
          (this.viewingAsUser && this.purpose !== 'show')) &&
        !this.hireApprovalStateIsPending
      )
    },
    showRequestButton() {
      return (
        !this.editing &&
        this.viewingAsCompany &&
        !this.dialogIsClosed &&
        !this.job.is_completed &&
        !this.hireApprovalStateIsPending
      )
    },
    showUndoAllButton() {
      return (
        this.viewingAsCompany && this.purpose !== 'create' && this.isAccepted && this.hasChanges
      )
    },
    canCancelPendingRequest() {
      return (
        this.viewingAsCompany && this.showSubmitButton && this.hasDraftContract && this.isAccepted
      )
    },
    companyContactsCount() {
      return Object.keys(this.companyContacts).length
    },
    companyRecruitersCount() {
      return Object.keys(this.companyRecruiters).length
    },
    canEditRate() {
      if (!this.editing || !this.viewingAsCompany) {
        return false
      }
      return this.isDirectHire || this.isAccepted
    },
    showRecruiterAttributionSection() {
      return (
        this.editing &&
        !this.isRequestingContractChanges &&
        this.viewingAsCompany &&
        ((!this.hasHiresWithCompanyRecruiter && !this.hasExpiredHires) ||
          this.submittingSupplier) &&
        this.companyRecruitersCount > 0
      )
    },
    shouldShowSignature() {
      // Don't show if we are simply viewing a contract which does not have a signature saved
      // This is because prior existing contracts may have been made before we used signatures
      if (this.purpose === 'show' && !this.activeContract?.freelancer_signature) {
        return false
      }
      return (
        ((this.isApproving && !this.viewingAsCompany) || (this.isAccepted && !this.editing)) &&
        !this.canceledJob
      )
    },
    hasHireApprovalState() {
      return (
        this.hireApprovalState !== 'unknown' &&
        this.hireApprovalState !== '' &&
        typeof this.hireApprovalState === 'string'
      )
    },
    hireApprovalStateIsPending() {
      return this.hasHireApprovalState && this.hireApprovalState === 'requested'
    },
    showCurrencyAlert() {
      return this.currencyConfig !== null
        ? this.currencyConfig.newCurrency === this.currencyConfig.flCurrency
        : false
    },
  },

  watch: {
    standardContract(newValue) {
      if (newValue) {
        this.$emit('use-standard-contract')
      } else {
        this.$emit('dont-use-standard-contract')
      }
    },
  },

  created() {
    this.editing =
      this.viewingAsCompany &&
      (this.hasDraftContract || this.purpose !== 'show') &&
      !this.canceledJob &&
      !this.hireApprovalStateIsPending
  },

  methods: {
    edit() {
      this.editing = true
      this.purpose = 'edit'
    },
    changesAdded() {
      this.hasChanges = true
    },
    changesCleared() {
      this.hasChanges = false
    },
    changesCounted(count) {
      this.changesCount = count
    },
    changesNeedingApprovalAdded() {
      this.hasChangesNeedingApproval = true
    },
    changesNeedingApprovalCleared() {
      this.hasChangesNeedingApproval = false
    },
    toggleWarnings(containsWarnings) {
      this.hasValidationErrors = containsWarnings
    },
    cancelChangeRequest() {
      this.cancellingChangeRequest = true
      nextTick(() => this.$refs.contractForm.submit())
    },
    addAdvanceField(fieldName) {
      this.advancedFields = [...new Set(this.advancedFields).add(fieldName)]
      $('#advancedFieldsWrapper button').click()
      if (fieldName === 'attachments') {
        setTimeout(() => {
          $('#attachments').focus()
        }, 500)
      }
    },
    handleTermsAccepted(value) {
      this.termsAccepted = value
    },
    onSubmitForm() {
      this.submitting = true
      this.$refs.contractForm.submit()
    },
    currencyChanged(currencyConfig) {
      this.currencyConfig = currencyConfig
    },
    clearErrors({ key }) {
      this.backEndErrors[key] = null
    },
  },
}
</script>

<style scoped lang="scss"></style>
